/**
 * The frame the community pages share: the server-side load (common data + the page's
 * own DealHunt read, with the edge-cache contract) and the `PageContainer` + head wrapper.
 *
 * Why `getServerSideProps` and not ISR: same reasons as the product route — the body is the
 * same for every visitor (reader-specific parts hydrate client-side: `YouCard`), it varies
 * only by path and the whitelisted query params, and the CDN holds it for 5 minutes. A
 * publish shows up within that window plus DealHunt's own 5-minute shared cache.
 */
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import dynamic from 'next/dynamic'
import PageContainer from 'shared-components/service/PageContainer'
import type { DSCommonData } from 'shared-definitions/types'
import Layout from 'components/Layout'
import { getds } from 'ds'
import { publicConfig } from 'config'
import { buildMetaTags } from 'lib/dealhunt/head'
import { useMarkCommunitySeen } from 'components/community/useCommunityPill'

/** The one-time "Show your name and photo?" question (member identity, 2026-10-10): client-only,
 *  so the shared-cached HTML of every community page stays the same for every reader. */
const IdentityBanner = dynamic(() => import('components/community/IdentityBanner'), {
  ssr: false,
})

/** 5 min at the edge, a day of stale-while-revalidate — the board moves when someone
 *  publishes, which is rare, and a stale rank for a few minutes misleads nobody. */
export const COMMUNITY_CACHE_CONTROL = 'public, s-maxage=300, stale-while-revalidate=86400'

export interface CommunityPageProps<T> {
  common: DSCommonData
  ts: number
  preview: null
  data: T
}

/**
 * `load` returns null for "does not exist" (an unknown member; DealHunt without the community
 * endpoints) → 404, uncached. A transient DealHunt failure THROWS (`UpstreamError`) and is
 * left to propagate → a retryable 500, never a 404 that would tell a crawler the page is gone.
 */
export async function communityServerProps<T>(
  context: GetServerSidePropsContext,
  load: () => Promise<T | null>
): Promise<GetServerSidePropsResult<CommunityPageProps<T>>> {
  const dataSource = await getds()
  const [{ data: common }, data] = await Promise.all([dataSource.getCommonData(), load()])
  if (data === null) {
    return { notFound: true }
  }
  context.res.setHeader('Cache-Control', COMMUNITY_CACHE_CONTROL)
  return { props: { common, ts: Date.now(), preview: null, data } }
}

/** One value of a query param, or null: `?period=all&period=month` is not a view we serve. */
export function queryValue(value: string | string[] | undefined): string | null {
  return typeof value === 'string' && value ? value : null
}

interface CommunityShellProps {
  common: DSCommonData
  ts: number
  /** Root-relative, with the app's trailing-slash convention, WITHOUT query. */
  path: string
  title: string
  description: string
  /** false → `noindex, follow`. */
  index?: boolean
  /** `og:image` — an `https:` URL or nothing (pass it through `safeImageUrl`). */
  imageUrl?: string | null
  /**
   * The commentHub container the page's `<Comments />` mounts (`PostContext`): a review's thread
   * sets `owner-review:<note id>`, a discussion's `discussion:<id>`; every other community page
   * leaves it null and has no comments. commentHub registers a container on its first lookup, with this page's
   * `<title>` and canonical URL as the post's title and link.
   */
  engagementContainerId?: string | null
  /** Structured data for the page's head (`<script type="application/ld+json">`, written raw by
   *  `LdJsonList`: user text in it must already be escaped — `discussionJsonLd`). */
  jsonLd?: readonly Record<string, unknown>[]
}

export const CommunityShell: React.FC<React.PropsWithChildren<CommunityShellProps>> = ({
  common,
  ts,
  path,
  title,
  description,
  index = true,
  imageUrl = null,
  engagementContainerId = null,
  jsonLd = [],
  children,
}) => {
  // The reader has reached Community: the header's day/week pill rests (client-only effect).
  useMarkCommunitySeen()
  const appUrl = publicConfig('appUrl') ?? ''
  const metaTitle = `${title} - SoundGuys`
  return (
    <PageContainer
      layoutProps={{ hideDealBar: true }}
      layout={Layout}
      props={{
        preview: null,
        page: {
          sections: null,
          ampPagePath: null,
          head: {
            metaTitle,
            metaTags: buildMetaTags({ title: metaTitle, description, imageUrl, index }),
            canonicalLink: `${appUrl}${path}`,
            jsonLd,
          },
          pagePath: path,
          meta: {
            engagementContainerId,
            disabledAds: false,
            topics: [],
            dataLayer: null,
            subscribeTags: [],
            tags: [],
            categories: [],
            postId: null,
            sponsor: null,
            sponsored: false,
          },
        },
        common,
        ts,
        rss: false,
      }}
    >
      <IdentityBanner />
      {children}
    </PageContainer>
  )
}
